/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import ContentFrame from '../../applicationFrame/ContentFrame';
import { ScrollAwareScroller, ScrollAwareContext, ScrollAwareAppNavStep } from '../../components/ScrollAware';
import ContentWrapper from '../../components/ContentWrapper';

import ZeroStateTracking from '../../components/icons/ZeroStateTracking';

import { TrackableOccurrenceAppNav } from './components/OccurrenceAppNav';
import ObservationSeries from './components/ObservationSeries';
import { TrackableFooter } from './components/OccurrenceFooter';

import { useNavigate } from '../../hooks/useNavigate';
import RouteNames from '../../appAuthorized/RouteNames';
import ContentLoader from '../../components/ContentLoader';
import { useShareCardListForActiveProfile } from '../../hooks/useShareCard';
import { useLists } from '../../hooks/useLists';
import { useExistingTrackableForActiveProfile } from '../../hooks/useTrackable';
import { TrackableComments } from './components/OccurrenceComments';
import { TrackableContacts } from './components/OccurrenceContacts';
import { TrackableFiles } from './components/OccurrenceFiles';
import { TrackableShortcut } from './components/OccurrenceShortcut';
import { Card, Icons } from '../../../bphComponents/bundle';
import COLORS from '../../styles/Colors';
import { GrayTextMarkdown } from '../../components/Markdown';
import { TrackableShareCards } from './components/HEShareCards';
import { CreateObservationNotifications } from './components/TrackableReminders';

interface Props {
  match: any;
}

const TrackableView: React.FC<Props> = (props) => {
  const trackableId = Number(props.match.params.trackableId);
  const isNew = false;

  const [{ trackable, trackableTermLookup, ...trackableData }, trackableActions] = useExistingTrackableForActiveProfile(trackableId);
  const [, navActions] = useNavigate();
  const [scData] = useShareCardListForActiveProfile();
  const [listData] = useLists();
  const isLoaded = trackableData.isLoaded && scData.isLoaded && listData.isLoaded;
  const trackableTerm = trackableTermLookup[trackable.trackable_term_id];
  const trackableTermName = trackableTerm ? trackableTerm.name : '';

  const [isBusy, setIsBusy] = React.useState(false);

  return (
    <div>
      <ScrollAwareContext>
        <ScrollAwareAppNavStep
          submitDisabled={false}
          cancel={null}
          done={() => { navActions.goBack(); }}
          color="white"
        />

        <ContentFrame className="bkgd-color-light-neutral">
          <ScrollAwareScroller>

            <TrackableOccurrenceAppNav
              trackableTermName={trackableTermName}
            />

            <ContentWrapper>
              <ContentLoader isContentLoaded={isLoaded && !isBusy} blockRenderUntilLoaded={!isLoaded} isFixed={true}>

                <ObservationSeries
                  trackable={trackable}
                  trackableTermName={trackableTermName}
                  EmptyIcon={ZeroStateTracking}
                  onClickAdd={() => {
                    trackableActions.apiCreateObservation().then((newObservation) => {
                      navActions.goTo(RouteNames.trackableObservation(trackableId, newObservation.id));
                    });
                  }}
                  onClickItem={(d) => {
                    navActions.goTo(RouteNames.trackableObservation(trackableId, d.id));
                  }}
                />

                <Card.CardShape>
                  <TrackableComments trackable={trackable} />
                  <TrackableContacts trackable={trackable} />
                </Card.CardShape>

                <TrackableFiles trackable={trackable} />

                <TrackableShareCards
                  trackable={trackable}
                  shareCardsDefaultExpanded={isNew}
                />

                <Card.ExternalCardTitle>
                  <FormattedMessage id="heOccList.trackingSettings.label" />
                  <Icons.IconTracking.Small />
                </Card.ExternalCardTitle>
                <Card.CardShape>
                  <TrackableShortcut
                    trackable={trackable}
                    onToggleShortcut={() => {
                      trackableActions.apiToggleShortcut();
                    }}
                  />

                  <CreateObservationNotifications
                    trackableId={trackableId}
                    trackable={trackable}
                    trackableTermLookup={trackableTermLookup}
                  />
                </Card.CardShape>

                <Card.CardShape isFlat={true} background={COLORS.gsLight}>
                  <Card.CardContent>
                    <GrayTextMarkdown phraseId="heOccList.trackingHelp.text.md" />
                  </Card.CardContent>
                </Card.CardShape>

                <TrackableFooter
                  trackable={trackable}
                  onDelete={() => {
                    setIsBusy(true);
                    trackableActions.apiDeleteTrackable()
                      .then(() => {
                        navActions.goBack(RouteNames.myBackpack());
                      }).catch(() => {
                        setIsBusy(false);
                      });
                  }}
                  onToggleFlag={() => {
                    const t = trackableActions.instantiate();
                    t.toggleFlagged();
                    trackableActions.apiUpdate(t.data);
                  }}
                />

              </ContentLoader>
            </ContentWrapper>
          </ScrollAwareScroller>

        </ContentFrame>
      </ScrollAwareContext>
    </div>
  );
};

export default TrackableView;
